/**
 * useTrackerCampaignQueries — the campaign list and the picked campaign's
 * flows for the tracker chosen in "Add a campaign" and "Paste config to a
 * campaign". Voluum and Binom run exactly as they always did: both campaign
 * lists eagerly, flows only for the picked tracker. The Tres Commas list and
 * flows are fetched only once Tres Commas is picked (its ids equal Voluum's,
 * so its list must never stand in for another tracker's).
 * For Media Devoted | Created By Ciro Autiero
 */

import { matchTracker, type TrackerId } from '@trafficpulse/shared';

import { useBinomCampaignFlows, useBinomCampaigns } from '@/hooks/binom/useBinom';
import { useTresCommasCampaignFlows, useTresCommasCampaigns } from '@/hooks/tres-commas';
import { useCampaignFlows, useCampaigns } from '@/hooks/voluum/useVoluum';

/** The parts of a campaign / flow list query the pickers read. */
export interface NamedListQuery {
  data?: Array<{ id: string; name: string }>;
  isLoading: boolean;
  isError: boolean;
  refetch: () => unknown;
}

export interface TrackerCampaignQueries {
  campaigns: NamedListQuery;
  flows: NamedListQuery;
}

/** The picked tracker gets the campaign id; every other tracker '' (= disabled). */
function flowCampaignIds(provider: TrackerId, campaignId: string): Record<TrackerId, string> {
  const none: Record<TrackerId, string> = { voluum: '', binom: '', 'tres-commas': '' };
  return { ...none, [provider]: campaignId };
}

export function useTrackerCampaignQueries(
  provider: TrackerId,
  campaignId: string,
): TrackerCampaignQueries {
  const ids = flowCampaignIds(provider, campaignId);
  const voluumCampaigns = useCampaigns();
  const binomCampaigns = useBinomCampaigns();
  const voluumFlows = useCampaignFlows(ids.voluum);
  const binomFlows = useBinomCampaignFlows(ids.binom);
  const tresCommasPicked = matchTracker(provider, {
    voluum: () => false,
    binom: () => false,
    'tres-commas': () => true,
  });
  const tresCommasCampaigns = useTresCommasCampaigns(tresCommasPicked);
  const tresCommasFlows = useTresCommasCampaignFlows(ids['tres-commas']);
  return matchTracker<TrackerCampaignQueries>(provider, {
    voluum: () => ({ campaigns: voluumCampaigns, flows: voluumFlows }),
    binom: () => ({ campaigns: binomCampaigns, flows: binomFlows }),
    'tres-commas': () => ({ campaigns: tresCommasCampaigns, flows: tresCommasFlows }),
  });
}
